import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  updateAccountProfile,
  changePassword,
  fetchUpgradeInfo,
  verifyUpgradePayment,
  fetchDowngradeInfo,
  downgradePackage,
  updateEntityDetails,
} from '../api/client';
import { loadPaystackScript } from '../utils/loadPaystack';
import { downloadInvoicePdf } from '../utils/generateInvoicePdf';
import { isAdvancedPackage } from '../utils/plan';
import CheckoutModal from './CheckoutModal';
import Modal from './Modal';
import './AccountPanel.css';

const DOWNGRADE_CONFIRM_PHRASE = 'downgrade package';

const BASIC_FEATURES = ['Text chat'];
const ADVANCED_FEATURES = [
  'Text chat',
  'Read & write (document analysis)',
  'Image generation',
  'API integration',
];

function formatKES(amount) {
  return `KES ${Number(amount || 0).toLocaleString()}`;
}

export default function AccountPanel() {
  const { user, updateUser } = useAuth();
  const isOrgAdmin = user.userType === 2 && user.role === 1;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ fullname: user.fullname || '', username: user.username || '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [passwordChanged, setPasswordChanged] = useState(false);

  const [editingEntity, setEditingEntity] = useState(false);
  const [entityForm, setEntityForm] = useState({
    name: user.entity?.name || '',
    email: user.entity?.email || '',
  });
  const [savingEntity, setSavingEntity] = useState(false);
  const [entitySaveError, setEntitySaveError] = useState('');

  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);

  const [downgradeInfo, setDowngradeInfo] = useState(null);
  const [downgradeLoading, setDowngradeLoading] = useState(true);
  const [downgrading, setDowngrading] = useState(false);
  const [downgradeError, setDowngradeError] = useState('');
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [downgradeConfirmText, setDowngradeConfirmText] = useState('');

  const loadUpgradeInfo = useCallback(async () => {
    setUpgradeLoading(true);
    try {
      const data = await fetchUpgradeInfo();
      setUpgradeInfo(data);
    } catch {
      setUpgradeInfo({ eligible: false });
    } finally {
      setUpgradeLoading(false);
    }
  }, []);

  const loadDowngradeInfo = useCallback(async () => {
    setDowngradeLoading(true);
    try {
      const data = await fetchDowngradeInfo();
      setDowngradeInfo(data);
    } catch {
      setDowngradeInfo({ eligible: false });
    } finally {
      setDowngradeLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUpgradeInfo();
    loadDowngradeInfo();
  }, [loadUpgradeInfo, loadDowngradeInfo]);

  function startEditing() {
    setForm({ fullname: user.fullname || '', username: user.username || '' });
    setSaveError('');
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setSaveError('');
  }

  async function handleSave() {
    const fullname = form.fullname.trim();
    const username = form.username.trim();
    if (!fullname || !username) {
      setSaveError('Name and username are required');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const data = await updateAccountProfile(fullname, username);
      updateUser(data.user);
      setEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  }

  function openChangePasswordModal() {
    setPasswordForm({ current: '', next: '', confirm: '' });
    setChangePasswordError('');
    setPasswordChanged(false);
    setShowChangePassword(true);
  }

  function closeChangePasswordModal() {
    setShowChangePassword(false);
  }

  async function handleChangePassword() {
    const { current, next, confirm } = passwordForm;
    if (!current || !next || !confirm) {
      setChangePasswordError('All fields are required');
      return;
    }
    if (next !== confirm) {
      setChangePasswordError("New passwords don't match");
      return;
    }
    if (next.length < 8) {
      setChangePasswordError('New password must be at least 8 characters');
      return;
    }
    setChangingPassword(true);
    setChangePasswordError('');
    try {
      await changePassword(current, next);
      setPasswordChanged(true);
    } catch (err) {
      setChangePasswordError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  }

  function startEditingEntity() {
    setEntityForm({ name: user.entity?.name || '', email: user.entity?.email || '' });
    setEntitySaveError('');
    setEditingEntity(true);
  }

  function cancelEditingEntity() {
    setEditingEntity(false);
    setEntitySaveError('');
  }

  async function handleSaveEntity() {
    const name = entityForm.name.trim();
    const email = entityForm.email.trim();
    if (!name || !email) {
      setEntitySaveError('Organization name and email are required');
      return;
    }
    setSavingEntity(true);
    setEntitySaveError('');
    try {
      const data = await updateEntityDetails(name, email);
      updateUser(data.user);
      setEditingEntity(false);
    } catch (err) {
      setEntitySaveError(err.message || 'Failed to update organization details');
    } finally {
      setSavingEntity(false);
    }
  }

  function openDowngradeConfirm() {
    setDowngradeError('');
    setDowngradeConfirmText('');
    setShowDowngradeConfirm(true);
  }

  function closeDowngradeConfirm() {
    setShowDowngradeConfirm(false);
    setDowngradeConfirmText('');
  }

  async function handleConfirmDowngrade() {
    if (!downgradeInfo?.eligible) return;
    setDowngrading(true);
    setDowngradeError('');
    try {
      const data = await downgradePackage();
      updateUser(data.user);
      closeDowngradeConfirm();
      loadUpgradeInfo();
      loadDowngradeInfo();
    } catch (err) {
      setDowngradeError(err.message || 'Failed to downgrade your plan');
    } finally {
      setDowngrading(false);
    }
  }

  function handleDownloadUpgradeInvoice() {
    if (!upgradeInfo?.eligible) return;
    const today = new Date();
    const periodEnd = new Date(today);
    periodEnd.setMonth(periodEnd.getMonth() + 1);

    downloadInvoicePdf(
      {
        id: user.id,
        amount: upgradeInfo.targetPackage.amount,
        currency: upgradeInfo.currency,
        status: 'pending',
        periodStart: today,
        periodEnd,
        createdAt: today,
      },
      user,
      {
        description: `Upgrade to ${upgradeInfo.targetPackage.name}`,
        invoiceNumber: `UPG-${String(user.id).padStart(6, '0')}`,
      }
    );
  }

  async function handleUpgrade() {
    if (!upgradeInfo?.eligible) return;
    setUpgradeError('');
    setShowCheckout(false);
    setUpgrading(true);
    try {
      const PaystackPop = await loadPaystackScript();
      const reference = `piua_upg${user.id}_${Date.now()}`;

      const handler = PaystackPop.setup({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: user.email,
        amount: upgradeInfo.chargeAmount * 100,
        currency: upgradeInfo.currency,
        ref: reference,
        metadata: { userId: user.id, upgradeTo: upgradeInfo.targetPackage.id },
        callback: (response) => {
          verifyUpgradePayment(response.reference)
            .then((data) => {
              updateUser(data.user);
              return Promise.all([loadUpgradeInfo(), loadDowngradeInfo()]);
            })
            .catch((err) => setUpgradeError(err.message || 'Upgrade could not be verified'))
            .finally(() => setUpgrading(false));
        },
        onClose: () => setUpgrading(false),
      });

      handler.openIframe();
    } catch (err) {
      setUpgradeError(err.message || 'Unable to start upgrade payment');
      setUpgrading(false);
    }
  }

  const features = isAdvancedPackage(user.packageName) ? ADVANCED_FEATURES : BASIC_FEATURES;

  return (
    <>
      <div className="account-section">
        <div className="account-section-header">
          <h3>Account details</h3>
          {!editing && (
            <button className="account-link-btn" onClick={startEditing}>
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="account-form">
            <label className="account-field">
              <span>Full name</span>
              <input
                value={form.fullname}
                onChange={(e) => setForm((f) => ({ ...f, fullname: e.target.value }))}
              />
            </label>
            <label className="account-field">
              <span>Username</span>
              <input
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              />
            </label>
            <label className="account-field">
              <span>Email</span>
              <input value={user.email || ''} disabled />
            </label>
            {saveError && <div className="account-error">{saveError}</div>}
            <div className="account-form-actions">
              <button className="account-btn-secondary" onClick={cancelEditing} disabled={saving}>
                Cancel
              </button>
              <button className="account-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="account-details-grid">
            <div>
              <span className="account-detail-label">Full name</span>
              <span className="account-detail-value">{user.fullname}</span>
            </div>
            <div>
              <span className="account-detail-label">Username</span>
              <span className="account-detail-value">{user.username}</span>
            </div>
            <div>
              <span className="account-detail-label">Email</span>
              <span className="account-detail-value">{user.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className="account-section">
        <h3>Security</h3>
        <button className="account-btn-secondary" onClick={openChangePasswordModal}>
          Change password
        </button>
      </div>

      {isOrgAdmin && (
        <div className="account-section">
          <div className="account-section-header">
            <h3>Organization details</h3>
            {!editingEntity && (
              <button className="account-link-btn" onClick={startEditingEntity}>
                Edit
              </button>
            )}
          </div>

          {editingEntity ? (
            <div className="account-form">
              <label className="account-field">
                <span>Organization name</span>
                <input
                  value={entityForm.name}
                  onChange={(e) => setEntityForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="account-field">
                <span>Organization email</span>
                <input
                  value={entityForm.email}
                  onChange={(e) => setEntityForm((f) => ({ ...f, email: e.target.value }))}
                />
              </label>
              {entitySaveError && <div className="account-error">{entitySaveError}</div>}
              <div className="account-form-actions">
                <button className="account-btn-secondary" onClick={cancelEditingEntity} disabled={savingEntity}>
                  Cancel
                </button>
                <button className="account-btn-primary" onClick={handleSaveEntity} disabled={savingEntity}>
                  {savingEntity ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="account-details-grid">
              <div>
                <span className="account-detail-label">Organization name</span>
                <span className="account-detail-value">{user.entity?.name || '—'}</span>
              </div>
              <div>
                <span className="account-detail-label">Organization email</span>
                <span className="account-detail-value">{user.entity?.email || '—'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="account-section">
        <h3>Subscription</h3>
        <div className="account-plan-card">
          <div className="account-plan-heading">
            <span className="account-plan-name">{user.packageName || 'No plan'}</span>
            {user.entity && <span className="account-plan-entity">{user.entity.name}</span>}
          </div>
          <ul className="account-feature-list">
            {features.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>

        {upgradeError && <div className="account-error">{upgradeError}</div>}

        {!upgradeLoading && upgradeInfo?.eligible && (
          <div className="account-upgrade-card">
            <div>
              <div className="account-upgrade-title">Upgrade to {upgradeInfo.targetPackage.name}</div>
              <div className="account-upgrade-sub">
                Unlock Read &amp; write, image generation, and API integration
                {user.userType === 2 ? ', and unlimited users' : ''} —{' '}
                {formatKES(upgradeInfo.targetPackage.amount)}/month
              </div>
            </div>
            <button className="account-btn-primary" onClick={() => setShowCheckout(true)} disabled={upgrading}>
              {upgrading ? 'Processing…' : 'Upgrade'}
            </button>
          </div>
        )}

        {!upgradeLoading && !upgradeInfo?.eligible && upgradeInfo?.reason && (
          <div className="account-upgrade-note">{upgradeInfo.reason}</div>
        )}

        {downgradeError && !showDowngradeConfirm && <div className="account-error">{downgradeError}</div>}

        {!downgradeLoading && downgradeInfo?.eligible && (
          <div className="account-downgrade-card">
            <div>
              <div className="account-downgrade-title">Downgrade to {downgradeInfo.targetPackage.name}</div>
              <div className="account-downgrade-sub">
                Drop Read &amp; write, image generation, and API integration
                {user.userType === 2 ? ', and the unlimited-user limit' : ''} —{' '}
                {formatKES(downgradeInfo.targetPackage.amount)}/month
              </div>
            </div>
            <button className="account-btn-secondary" onClick={openDowngradeConfirm}>
              Downgrade
            </button>
          </div>
        )}
      </div>

      {showCheckout && upgradeInfo?.eligible && (
        <CheckoutModal
          title="Checkout"
          items={[
            {
              label: `Upgrade to ${upgradeInfo.targetPackage.name}`,
              amount: upgradeInfo.targetPackage.amount,
            },
          ]}
          currency={upgradeInfo.currency}
          processing={upgrading}
          error={upgradeError}
          onDownloadInvoice={handleDownloadUpgradeInvoice}
          onCheckout={handleUpgrade}
          onClose={() => setShowCheckout(false)}
        />
      )}

      {showDowngradeConfirm && downgradeInfo?.eligible && (
        <Modal title="Downgrade plan" onClose={closeDowngradeConfirm}>
          <p className="account-confirm-modal-text">
            You're about to downgrade to <strong>{downgradeInfo.targetPackage.name}</strong>.{' '}
            <strong>This action cannot be reversed</strong> — you'll lose Read &amp; write, image generation, and
            API integration{user.userType === 2 ? ', and the unlimited-user limit' : ''} immediately. Any future
            upgrade will require the normal payment.
          </p>
          <label className="account-confirm-input-field">
            <span>
              Type <strong>{DOWNGRADE_CONFIRM_PHRASE}</strong> to confirm
            </span>
            <input
              value={downgradeConfirmText}
              onChange={(e) => setDowngradeConfirmText(e.target.value)}
              disabled={downgrading}
              autoFocus
              autoComplete="off"
            />
          </label>
          {downgradeError && <div className="account-error">{downgradeError}</div>}
          <div className="account-confirm-modal-actions">
            <button className="account-btn-secondary" onClick={closeDowngradeConfirm} disabled={downgrading}>
              Cancel
            </button>
            <button
              className="account-btn-danger"
              onClick={handleConfirmDowngrade}
              disabled={downgrading || downgradeConfirmText.trim().toLowerCase() !== DOWNGRADE_CONFIRM_PHRASE}
            >
              {downgrading ? 'Downgrading…' : 'Downgrade plan'}
            </button>
          </div>
        </Modal>
      )}

      {showChangePassword && (
        <Modal title="Change password" onClose={closeChangePasswordModal}>
          {passwordChanged ? (
            <>
              <p className="account-confirm-modal-text">Your password has been updated.</p>
              <div className="account-confirm-modal-actions">
                <button className="account-btn-primary" onClick={closeChangePasswordModal}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="account-modal-form">
                <label className="account-field">
                  <span>Current password</span>
                  <input
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm((f) => ({ ...f, current: e.target.value }))}
                    disabled={changingPassword}
                    autoFocus
                    autoComplete="current-password"
                  />
                </label>
                <label className="account-field">
                  <span>New password</span>
                  <input
                    type="password"
                    value={passwordForm.next}
                    onChange={(e) => setPasswordForm((f) => ({ ...f, next: e.target.value }))}
                    disabled={changingPassword}
                    autoComplete="new-password"
                  />
                </label>
                <label className="account-field">
                  <span>Confirm new password</span>
                  <input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
                    disabled={changingPassword}
                    autoComplete="new-password"
                  />
                </label>
                {changePasswordError && <div className="account-error">{changePasswordError}</div>}
              </div>
              <div className="account-confirm-modal-actions">
                <button className="account-btn-secondary" onClick={closeChangePasswordModal} disabled={changingPassword}>
                  Cancel
                </button>
                <button className="account-btn-primary" onClick={handleChangePassword} disabled={changingPassword}>
                  {changingPassword ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
